/**
 * API Configuration
 * Centralized configuration for all API endpoints and settings
 *
 * Environment variables:
 * - VITE_API_BASE_URL: Base URL for the API server
 * - VITE_API_TIMEOUT: Request timeout in milliseconds (default: 30000)
 */

import { calculateApiUrl } from '@bizfirst/common-js';

export { calculateApiUrl };

/**
 * API Configuration Interface
 */
export interface ApiConfig {
  baseUrl: string;
  timeout: number;
  tenantId: number;
  headers: Record<string, string>;
}

/**
 * Get environment variable with fallback
 */
const getEnvVar = (key: string, defaultValue: string): string => {
  return String(import.meta.env[key] ?? defaultValue);
};

/**
 * Default API Configuration
 */
export const apiConfig: ApiConfig = {
  baseUrl: calculateApiUrl(getEnvVar('VITE_API_BASE_URL', '')),
  timeout: parseInt(getEnvVar('VITE_API_TIMEOUT', '30000'), 10),
  tenantId: parseInt(getEnvVar('VITE_TENANT_ID', '1'), 10),
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
};

/**
 * Update API configuration dynamically
 */
export const updateApiConfig = (config: Partial<ApiConfig>): void => {
  Object.assign(apiConfig, config);
};

/**
 * Get current API configuration
 */
export const getApiConfig = (): Readonly<ApiConfig> => {
  return Object.freeze({ ...apiConfig });
};
